import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { MaterialModule } from "src/app/material.module";
import {
  isBillingOverviewCancellationPending,
  resolveBillingOverviewCancellationEndDateLabel,
  resolveBillingOverviewNextTierLabel,
  resolveBillingOverviewUsageProgressPercent,
} from "../billing-service-overview.mapper";
import { UsageOverviewDialogService } from "../usage-overview-dialog.service";
import { UsageOverviewReverseCancellationService } from "../usage-overview-reverse-cancellation.service";
import { toTranslateParams } from "src/app/shared/helpers/translate-params.helper";

@Component({
  selector: "app-usage-overview-sidenav",
  standalone: true,
  imports: [MaterialModule, TranslateModule],
  templateUrl: "./usage-overview-sidenav.component.html",
  styleUrls: ["./usage-overview-sidenav.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageOverviewSidenavComponent {
  protected readonly usageOverviewDialog = inject(UsageOverviewDialogService);
  protected readonly reverseCancellation = inject(
    UsageOverviewReverseCancellationService,
  );
  protected readonly tParams = toTranslateParams;

  protected readonly claimCount = computed(
    () => this.usageOverviewDialog.overview()?.usage.claimCount ?? 0,
  );

  protected readonly currentServicePlan = computed(
    () => this.usageOverviewDialog.overview()?.currentServicePlan ?? null,
  );

  protected readonly tierMarkerThreshold = computed(() => {
    const plan = this.currentServicePlan();
    if (!plan) {
      return null;
    }

    return resolveBillingOverviewNextTierLabel(plan);
  });

  protected readonly progressPercent = computed(() => {
    const overview = this.usageOverviewDialog.overview();
    if (!overview) {
      return 0;
    }

    return resolveBillingOverviewUsageProgressPercent(overview);
  });

  protected readonly cancellationPending = computed(() =>
    isBillingOverviewCancellationPending(this.usageOverviewDialog.overview()),
  );

  protected readonly cancellationEndDateLabel = computed(() => {
    const overview = this.usageOverviewDialog.overview();
    if (!overview || !isBillingOverviewCancellationPending(overview)) {
      return null;
    }

    return resolveBillingOverviewCancellationEndDateLabel(overview);
  });

  protected openOverview(event: Event): void {
    if (this.shouldIgnoreBodyClick(event)) {
      return;
    }

    this.usageOverviewDialog.restore();
  }

  protected refresh(event: Event): void {
    event.stopPropagation();
    this.usageOverviewDialog.refresh().subscribe();
  }

  protected keepService(event: Event): void {
    this.reverseCancellation.keepService(event);
  }

  protected unpin(event: Event): void {
    event.stopPropagation();
    this.usageOverviewDialog.unpinFromSidenav();
  }

  private shouldIgnoreBodyClick(event: Event): boolean {
    return Boolean(
      (event.target as HTMLElement | null)?.closest(
        ".usage-overview-sidenav__icon-button, .usage-overview-sidenav__keep-button",
      ),
    );
  }
}
